import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import { sortLocalized, type LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Button from '../../../components/ui/Button'
import Badge from '../../../components/ui/Badge'
import type { Grant } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  busyId: number | null
  onEdit: (row: Grant) => void
  onToggle: (row: Grant, active: boolean) => void
  onDelete: (row: Grant) => void
  /** Salt okunur kip (126, Kısıtlı Yetkili asistan): switch görünür ama pasif; Düzenle/Sil çizilmez. */
  readOnly?: boolean
}

/** Yetki (GRANTS) tablosu sütunları. Durum satır-içi InputSwitch (lookup kalıbı) + düzenle/sil. */
export function grantColumns({
  t,
  localized,
  busyId,
  onEdit,
  onToggle,
  onDelete,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  return [
    <Column
      key="name"
      header={t('admin.grants.columns.name')}
      // Sıralama GÖRÜNEN değere göre; `field` verilseydi İngilizce arayüzde TR adına göre
      // sıralanır, yani başlık kullanıcıya yanlış söylerdi.
      sortable
      sortFunction={(e) =>
        sortLocalized(e.data as Grant[], localized, (r) => [r.nameTr, r.nameEn], e.order)
      }
      body={(row: Grant) => localized(row.nameTr, row.nameEn)}
    />,
    <Column key="code" field="code" header={t('admin.grants.columns.code')} sortable />,
    <Column key="object" field="object" header={t('admin.grants.columns.object')} />,
    <Column
      key="roles"
      header={t('admin.grants.columns.roles')}
      // Sıralanabilir DEĞİL: çok değerli bir kolonda sıralama kullanıcıya anlamlı bir sıra üretmez
      // (Kullanıcı Yönetimi'ndeki Roller kolonu da sıralanamaz).
      body={(row: Grant) =>
        row.roles.length ? (
          <div className="admin-role-chips">
            {row.roles.map((r) => (
              <Badge key={r.id} tone="neutral">
                {localized(r.nameTr, r.nameEn)}
              </Badge>
            ))}
          </div>
        ) : (
          // Hiçbir role verilmemiş yetkide hücre BOŞ kalır; "Yok"/"—" basılmaz (FR-005).
          ''
        )
      }
    />,
    <Column
      key="status"
      header={t('admin.grants.columns.status')}
      style={{ width: '8rem' }}
      body={(row: Grant) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active ? t('admin.grants.status.active') : t('admin.grants.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('admin.grants.columns.actions')}
      style={{ width: '10rem' }}
      body={(row: Grant) => {
        const active = row.recordStatus === 1
        return (
          <div className="admin-row-actions">
            <InputSwitch
              className="admin-switch"
              checked={active}
              disabled={readOnly || busyId === row.id}
              onChange={(e) => onToggle(row, Boolean(e.value))}
              aria-label={row.nameTr}
              tooltip={t('admin.grants.actions.toggle')}
              tooltipOptions={{ position: 'bottom' }}
            />
            {!readOnly && (
              <>
                <Button
                  icon="pi pi-pencil"
                  rounded
                  text
                  aria-label={t('admin.grants.actions.edit')}
                  tooltip={t('admin.grants.actions.edit')}
                  tooltipOptions={{ position: 'bottom' }}
                  onClick={() => onEdit(row)}
                />
                <Button
                  icon="pi pi-trash"
                  rounded
                  text
                  severity="danger"
                  aria-label={t('admin.grants.actions.delete')}
                  tooltip={t('admin.grants.actions.delete')}
                  tooltipOptions={{ position: 'bottom' }}
                  disabled={busyId === row.id}
                  onClick={() => onDelete(row)}
                />
              </>
            )}
          </div>
        )
      }}
    />,
  ]
}
